import { useSelector } from "react-redux";
import { LaborCodeDTO, LaborLineDTO } from "../../../models/WarrantyClaimManagement";
import { useEffect, useMemo, useState } from "react";
import { CellProps, Column } from "react-table";
import { 
    Box, 
    Button, 
    Checkbox, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogTitle, 
    FormControl, 
    InputLabel, 
    MenuItem, 
    Select, 
    Stack, 
    TextField, 
    Typography 
} from "@mui/material";
import { apiStore } from "../../../api/ApiFactory";
import { ReactTable } from "../../../components/table/ReactTable";
import { LineStatus } from "../../../helpers/enums/Warranty";

interface IProps {
    isOpen: boolean;
    laborRate: number;
    onClose: () => void;
    onSelect: (records: LaborLineDTO[]) => void;
}

const AddLaborLines: React.FC<IProps> = ({isOpen, laborRate, onClose, onSelect}) => {

    const [searchTerm, setSearchTerm] = useState<string>('');

    const [selectedRows, setSelectedRows] = useState<LaborCodeDTO[]>([]); 
    const [tableData, setTableData] = useState<LaborCodeDTO[] | undefined>();

    const columns: Column<LaborCodeDTO>[] = useMemo(
        () => [
            {
                Header: "Selected",
                Cell: ({row}: CellProps<LaborCodeDTO>) => {

                    const isSelected = selectedRows.some(x => x.value === row.original.value);
                    row.isSelected = isSelected;

                    return (
                        <Checkbox
                            checked={row.isSelected}
                            onClick={() => {
                                toggleRowSelection(row.original);
                                row.toggleRowSelected();
                            }}
                        />
                    );
                },
            },
            {
                Header: "Code",
                accessor: "value"
            },
            {
                Header: "Description",
                accessor: "name"
            }
        ],
        [selectedRows]
    );

    const toggleRowSelection = (laborCode: LaborCodeDTO) => {

        let newSelectedRows = selectedRows;

        if (newSelectedRows.some(x => x.value === laborCode.value)) {
            //Delete record
            newSelectedRows = selectedRows.filter(x => x.value !== laborCode.value);
        } else {
            //Add record
            newSelectedRows = [...selectedRows, laborCode];
        }

        setSelectedRows(newSelectedRows);
    };

    useEffect(() => {

        if(isOpen) {

            if(tableData === undefined) {
                apiStore.lookupApi.getLaborCodes().then((x) => setTableData(x));
            }
        }
        else {
            //Clear out only if there are no selectedRows.
            if(selectedRows.length === 0) {
                setSearchTerm("");
            }
        }
    }, [isOpen]);

    const filteredData = useMemo(() => {

        return tableData?.filter(item => {

            let isValid = true;

            if(searchTerm != "") {
                isValid = 
                    item.value.toLowerCase().includes(searchTerm.toLowerCase()) || 
                    item.name.toLowerCase().includes(searchTerm.toLowerCase())
            }

            return isValid;
        })

    }, [tableData, searchTerm]);

    const onSave = () => {

        const selectedPartLines = selectedRows.map((item) => 
            {
                let lineitem: LaborLineDTO = {
                    lineNumber: 0,
                    lineStatusCode: LineStatus.PENDING,
                    laborCode: item,
                    hours: item.standardHours ?? 0,
                    rate: laborRate,
                    cost: laborRate * (item.standardHours ?? 0)
                }

                return lineitem;
            }
        );

        onSelect(selectedPartLines);
        setSelectedRows([]);
        onClose();
    };

    const onReset = () => {
        setSelectedRows([]);
        setSearchTerm("");
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    height: '100%',
                    right: 0,
                    position: 'fixed',
                    top: 0,
                    margin: 0,
                    maxHeight: '100%',
                },
            }}
        >
            <DialogTitle>
                <Stack direction="row" sx={{justifyContent: "space-between", alignItems: "center"}}>
                    <label>Labor Codes</label>
                    <Typography variant="subtitle1" className="info-header">
                        {selectedRows.length} Labor Codes Selected
                    </Typography>
                </Stack>
                
            </DialogTitle>
            
            <DialogContent dividers>
                <InputLabel>Enter search terms (Labor Code, Description)</InputLabel>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search terms"
                    onBlur={(e) => setSearchTerm(e.target.value)}
                    sx={{marginBottom:"16px"}}
                />

                {filteredData !== undefined && (
                    <ReactTable<LaborCodeDTO>
                        name="add-labor-code-list"
                        serverOnChange={false}
                        columns={columns}
                        data={filteredData!}
                        isLoading={false}
                        inlineLockState={false}
                    />
                )}

            </DialogContent>
            
            <DialogActions>
                <Button
                    onClick={onReset}
                    color="secondary"
                    sx={{
                        border: '1px solid #B0BEC5',
                        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                        '&:hover': {
                            boxShadow: '0px 6px 10px rgba(0, 0, 0, 0.2)',
                        }
                    }}
                >
                    Reset
                </Button>
                <Button
                    onClick={onClose}
                    color="secondary"
                    sx={{
                        border: '1px solid #B0BEC5',
                        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                        '&:hover': {
                            boxShadow: '0px 6px 10px rgba(0, 0, 0, 0.2)',
                        }
                    }}
                >
                    Close
                </Button>

                <Box ml={2}>
                    <Button
                        onClick={onSave}
                        variant="contained"
                        type="submit"
                        sx={{
                            border: '1px solid black',
                            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                            '&:hover': {
                                boxShadow: '0px 6px 10px rgba(0, 0, 0, 0.2)',
                            },
                            textTransform: 'none'
                        }}
                    >
                        Add Labor
                    </Button>
                </Box>
            </DialogActions>

        </Dialog>
    );


};


export default AddLaborLines;